import React, { useState } from 'react';
import { ClipboardList, X, Bot, User, Zap, Mail, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface StatusNoteFile {
  data: string;            // gs:// URI or base64
  preview?: string;        // public HTTPS URL
  mimeType: string;
  name?: string;
}

export interface StatusNote {
  ts: string;
  note: string;
  actor: string;
  step_type: string | null;
  /** Attachments (e.g. email-intake images) surfaced alongside the note. */
  uploadedFiles?: StatusNoteFile[];
}

/** Convert a gs:// URI to a public HTTPS URL. Passthrough for anything else. */
const toHttpUrl = (src: string): string => {
  if (src && src.startsWith('gs://')) {
    const withoutScheme = src.slice('gs://'.length);
    const slashIdx = withoutScheme.indexOf('/');
    if (slashIdx !== -1) {
      const bucket = withoutScheme.slice(0, slashIdx);
      const object = withoutScheme.slice(slashIdx + 1);
      return `https://storage.googleapis.com/${bucket}/${object}`;
    }
  }
  return src;
};

/** Inline attachments (images + PDFs/other docs) shown under an audit-log note. */
function NoteAttachments({ files }: { files?: StatusNoteFile[] }) {
  const all = files ?? [];
  const images = all.filter(f => f.mimeType?.startsWith('image/'));
  const docs = all.filter(f => !f.mimeType?.startsWith('image/'));
  if (images.length === 0 && docs.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {images.map((file, idx) => {
        const src = toHttpUrl(file.preview || file.data);
        return (
          <img
            key={`img-${idx}`}
            src={src}
            alt={file.name || `Image ${idx + 1}`}
            title={file.name}
            className="max-h-40 w-auto rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
          />
        );
      })}
      {docs.map((file, idx) => {
        const src = toHttpUrl(file.preview || file.data);
        return (
          <a
            key={`doc-${idx}`}
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            title={file.name}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileText className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
            <span className="truncate max-w-[160px]">{file.name || 'Attachment'}</span>
          </a>
        );
      })}
    </div>
  );
}

interface WorkflowAuditLogProps {
  notes: StatusNote[];
  className?: string;
  /** Maximum number of timeline entries shown in the modal (default: unlimited) */
  maxSteps?: number;
  /** When provided, the component is controlled: the modal is open when true */
  open?: boolean;
  /** Called when the modal requests to be closed (required when `open` is provided) */
  onClose?: () => void;
}

const STEP_TYPE_LABELS: Record<string, string> = {
  update_order_status: 'Status',
  run_integration:     'Integration',
  send_notification:   'Notification',
  result_rule:         'Rule',
  add_order_note:      'Note',
  intake:              'Email Intake',
};

function ActorIcon({ actor }: { actor: string }) {
  if (actor === 'workflow' || actor === 'automation') {
    return <Zap className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />;
  }
  if (actor === 'agent' || actor === 'system') {
    return <Bot className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />;
  }
  if (actor === 'system/email-intake' || actor === 'intake') {
    return <Mail className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />;
  }
  return <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />;
}

function stepBadgeClass(stepType: string | null): string {
  switch (stepType) {
    case 'update_order_status': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'run_integration':     return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'send_notification':   return 'bg-green-50 text-green-700 border-green-200';
    case 'result_rule':         return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'intake':              return 'bg-amber-50 text-amber-700 border-amber-200';
    default:                    return 'bg-gray-50 text-gray-600 border-gray-200';
  }
}

/** Render a plain-text note with basic markdown: **bold**, *italic*, bullet lines */
function NoteText({ text }: { text: unknown }) {
  const lines = String(text ?? '').split('\n');
  return (
    <div className="text-sm text-gray-800 leading-snug space-y-0.5">
      {lines.map((line, i) => {
        // Bullet lines starting with "- "
        const isBullet = line.trimStart().startsWith('- ');
        const content = isBullet ? line.trimStart().slice(2) : line;
        const rendered = content
          .split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g)
          .map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j}>{part.slice(2, -2)}</strong>;
            }
            if (part.startsWith('*') && part.endsWith('*')) {
              return <em key={j}>{part.slice(1, -1)}</em>;
            }
            return <span key={j}>{part}</span>;
          });
        if (isBullet) {
          return <div key={i} className="flex gap-1.5"><span className="text-gray-400 flex-shrink-0 mt-0.5">•</span><span>{rendered}</span></div>;
        }
        return <div key={i}>{rendered}</div>;
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal — full timeline
// ---------------------------------------------------------------------------
interface ModalProps {
  notes: StatusNote[];
  onClose: () => void;
  maxSteps?: number;
}

const WorkflowAuditLogModal: React.FC<ModalProps> = ({ notes, onClose, maxSteps }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const sorted = [...notes].sort((a, b) => (b.ts > a.ts ? 1 : -1));
  const limited = maxSteps && !showAll ? sorted.slice(0, maxSteps) : sorted;
  const hiddenCount = maxSteps ? sorted.length - maxSteps : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <ClipboardList className="w-4 h-4 text-purple-500" />
          <h2 className="font-semibold text-gray-900 flex-1">
            {t('orders.auditLog.title', { defaultValue: 'Workflow Log' })}
          </h2>
          <span className="text-xs font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
            {sorted.length} {t('orders.auditLog.entries', { defaultValue: 'entries' })}
          </span>
          <button
            onClick={onClose}
            className="ml-1 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Timeline */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          <ol className="relative border-l-2 border-gray-100 space-y-0 ml-2">
            {limited.map((entry, i) => (
              <li key={i} className="ml-5 pb-6 last:pb-0">
                {/* Dot */}
                <span className="absolute -left-[10px] flex items-center justify-center w-5 h-5 rounded-full bg-white border-2 border-gray-200 shadow-sm">
                  <ActorIcon actor={entry.actor} />
                </span>
                {/* Card */}
                <div className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 hover:border-gray-200 transition-colors">
                  <NoteText text={entry.note} />
                  <NoteAttachments files={entry.uploadedFiles} />
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {entry.step_type && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${stepBadgeClass(entry.step_type)}`}>
                        {STEP_TYPE_LABELS[entry.step_type] ?? entry.step_type}
                      </span>
                    )}
                    <span className="text-[11px] text-gray-400">
                      {new Date(entry.ts).toLocaleString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                    <span className="text-[10px] text-gray-400 capitalize ml-auto">
                      {entry.actor}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          {hiddenCount > 0 && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="mt-3 w-full text-center text-xs font-medium text-purple-600 hover:text-purple-800 py-1.5 border border-dashed border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
            >
              {t('orders.auditLog.showMore', { defaultValue: 'Show {{count}} more', count: hiddenCount })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Public component — trigger button that opens the modal
// ---------------------------------------------------------------------------
const WorkflowAuditLog: React.FC<WorkflowAuditLogProps> = ({ notes, className = '', maxSteps, open: controlledOpen, onClose }) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  // Support both controlled (open/onClose props) and uncontrolled (internal toggle) modes
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleClose = () => {
    if (onClose) onClose();
    else setInternalOpen(false);
  };

  if (!notes || notes.length === 0) return null;

  // Controlled mode: render only the modal, no trigger button
  if (controlledOpen !== undefined) {
    return isOpen ? <WorkflowAuditLogModal notes={notes} maxSteps={maxSteps} onClose={handleClose} /> : null;
  }

  return (
    <>
      <button
        onClick={e => { e.stopPropagation(); setInternalOpen(true); }}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-purple-200 bg-purple-50 hover:bg-purple-100 active:bg-purple-200 text-purple-700 text-xs font-medium transition-colors ${className}`}
        title={t('orders.auditLog.viewLog', { defaultValue: 'View workflow log' })}
      >
        <ClipboardList className="w-3 h-3" />
        {t('orders.auditLog.title', { defaultValue: 'Workflow Log' })}
        <span className="bg-purple-200 text-purple-800 rounded-full px-1.5 leading-4 text-[10px] font-bold">
          {notes.length}
        </span>
      </button>

      {isOpen && <WorkflowAuditLogModal notes={notes} maxSteps={maxSteps} onClose={handleClose} />}
    </>
  );
};

export default WorkflowAuditLog;
